import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FactCheckContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useFactCheck = () => {
  const context = useContext(FactCheckContext);
  if (!context) {
    throw new Error('useFactCheck must be used within a FactCheckProvider');
  }
  return context;
};

export const FactCheckProvider = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [factCheckResults, setFactCheckResults] = useState({}); // Map of messageId -> result
  const [pendingChecks, setPendingChecks] = useState(new Set()); // Message IDs being checked

  // Fetch initial config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/fact-check/config`);
        if (response.ok) {
          const data = await response.json();
          setIsEnabled(data.enabled);
        }
      } catch (err) {
        console.error('Error fetching fact check config:', err);
      }
    };
    fetchConfig();
    
    // Poll for config changes every 10 seconds
    const interval = setInterval(fetchConfig, 10000);
    return () => clearInterval(interval);
  }, []);

  // Function to check a message
  const checkMessage = useCallback(async (messageId, subjectId, question, answer) => {
    // Skip if disabled or already checking/checked
    if (!isEnabled || pendingChecks.has(messageId) || factCheckResults[messageId]) {
      return;
    }
    
    // Skip for default agent (no RAG to verify against)
    if (subjectId === 'default') {
      setFactCheckResults(prev => ({
        ...prev,
        [messageId]: { 
          status: 'skipped', 
          is_verified: true, 
          explanation: 'Agente general - no requiere verificación' 
        }
      }));
      return;
    }
    
    // Mark as pending
    setPendingChecks(prev => new Set([...prev, messageId]));
    
    try {
      // Add 1 second delay as requested
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const response = await fetch(`${API_URL}/api/chat/fact-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject_id: subjectId,
          user_question: question,
          agent_response: answer
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setFactCheckResults(prev => ({
          ...prev,
          [messageId]: { 
            status: 'completed', 
            ...result 
          }
        }));
      } else {
        setFactCheckResults(prev => ({
          ...prev,
          [messageId]: { 
            status: 'error', 
            is_verified: true, 
            explanation: 'Error en verificación' 
          }
        }));
      }
    } catch (err) {
      console.error('Fact check error:', err);
      setFactCheckResults(prev => ({
        ...prev,
        [messageId]: { 
          status: 'error', 
          is_verified: true, 
          explanation: 'Error de conexión' 
        }
      }));
    } finally {
      setPendingChecks(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  }, [isEnabled, pendingChecks, factCheckResults]);

  // Function to get result for a message
  const getResult = useCallback((messageId) => {
    if (pendingChecks.has(messageId)) {
      return { status: 'checking' };
    }
    return factCheckResults[messageId] || null;
  }, [pendingChecks, factCheckResults]);

  // Function to check if message is being checked
  const isChecking = useCallback((messageId) => {
    return pendingChecks.has(messageId);
  }, [pendingChecks]);

  const value = {
    isEnabled,
    checkMessage,
    getResult,
    isChecking,
    factCheckResults,
    setIsEnabled
  };

  return (
    <FactCheckContext.Provider value={value}>
      {children}
    </FactCheckContext.Provider>
  );
};

export default FactCheckContext;
